import React from 'react';
import { styled } from '@mui/material/styles';
import { List, ListItem, ListItemText } from '@mui/material';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import styles from './DiscountLevel.module.css';

interface Props {
  discountLevel: {
    parts_total_for_year: number,
    next_level: number,
    amount_to_next_level: number,
    discount_level: string
  }
}

export default function DiscountLevel(props: Props) {
  const [open, setOpen] = React.useState(false);

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  function calculateProgressWidth() {
    const decimal = props.discountLevel.parts_total_for_year / props.discountLevel.next_level;

    return (decimal * 100).toString() + '%';
  }

  const DiscountLevelsTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#f5f5f9',
      color: 'rgba(0, 0, 0, 0.87)',
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: '1px solid #dadde9',
    },
  }));

  function formatDiscountLevel(string: string) {
    if (!string) return '';
    return string
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  const formattedDiscountLevel = formatDiscountLevel(props.discountLevel.discount_level);

  return (
    <div className={styles.discount_container}>
      <h2 className={styles.discount_header}>
        Discount Level
      </h2>
      <div style={{ padding: 15 }}>
        <h4 className={styles.discount_label}>Your Current Volume Discount Purchasing Level:</h4>
        <div className={styles.discount_details}>
          <p className={styles.discount_text}>
            <b>365 Day Parts Total:</b> ${props.discountLevel.parts_total_for_year}
          </p>
          <p className={styles.discount_text}>
            <b>Volume Discount:</b> {formattedDiscountLevel}
          </p>
          <p className={styles.discount_text}>
            <b>Amount to Next Level:</b> ${props.discountLevel.amount_to_next_level}
          </p>
          <div className={styles.progress_bar}>
            <div
              style={{
                height: '100%',
                width: calculateProgressWidth(),
                backgroundImage: 'linear-gradient(#005e06, #38b85c, #005e06)'
              }}
            ></div>
          </div>
        </div>
        <ClickAwayListener onClickAway={handleTooltipClose}>
          <div>
            <DiscountLevelsTooltip
              onClose={handleTooltipClose}
              open={open}
              disableHoverListener
              placement="bottom"
              title={
                <>
                  <List dense>
                    <ListItem sx={{ margin: 0 }}>
                      <ListItemText
                        primary="Level 1 - $4,000 approx. 5% off per part"
                        primaryTypographyProps={{ fontSize: 12 }}
                      />
                    </ListItem>
                    <ListItem sx={{ margin: 0 }}>
                      <ListItemText
                        primary="Level 2 - $8,000 approx. 7% off per part"
                        primaryTypographyProps={{ fontSize: 12 }}
                      />
                    </ListItem>
                    <ListItem sx={{ margin: 0 }}>
                      <ListItemText
                        primary="Level 3 - $16,000 approx. 10% off per part"
                        primaryTypographyProps={{ fontSize: 12 }}
                      />
                    </ListItem>
                  </List>
                  <p className={styles.tooltip_text}>
                    <em>*The discount level is based on the amount purchased over a 365-day time period.</em>
                  </p>
                  <p className={styles.tooltip_text}>
                    <em>**The percentage discount per part could vary based on the part type and quantity levels.</em>
                  </p>
                </>
              }
            >
              <div
                onClick={handleTooltipOpen}
                className={styles.tooltip_trigger}
              >
                <h4 className={styles.tooltip_title}>More About Our Volume Discount Levels:</h4>
                <InfoOutlinedIcon className={styles.icon} />
              </div>
            </DiscountLevelsTooltip>
          </div>
        </ClickAwayListener>
      </div>
    </div>
  );
}